import React from 'react';
import {
  AllPlugins,
  type EditorPlugin,
  type EmbeddedWhiteboardElement,
  withHoverToolbar,
  slate,
  type ComponentProps,
} from '@aha-app/aha-editor-v2';
import { type RecordId } from '../../../../javascripts/shared-types';
import EmbeddedWhiteboardComponent from './components/EmbeddedWhiteboardComponent';
import EmbeddedWhiteboardPlaceholderComponent from './components/EmbeddedWhiteboardPlaceholderComponent';
import EmbeddedWhiteboardPasteTransformerPlugin from './EmbeddedWhiteboardPasteTransformerPlugin';
import {
  createWhiteboardRecord,
  insertWhiteboard,
} from 'components/aha_editor_field_v2/WhiteboardUtils';
import ToolEditWhiteboard from './tools/ToolEditWhiteboard';
import ToolDeleteWhiteboard from './tools/ToolDeleteWhiteboard';
import ToolOpenOriginalWhiteboard from './tools/ToolOpenOriginalWhiteboard';
import ToolShowWhiteboardComments from './tools/ToolShowWhiteboardComments';
import type WhiteboardContainerController from 'components/whiteboards/WhiteboardContainerController';
import { EmbeddedWhiteboardControllersPluginFactory } from './EmbeddedWhiteboardControllersPluginFactory';
import ToolOpenWhiteboard from './tools/ToolOpenWhiteboard';

interface EmbeddedWhiteboardPluginOptions {
  containingPageId?: RecordId;
  sharingOptions: any;
  defaultProject?: { id: RecordId; name: string };
  commentsEnabled?: boolean;
  canFacilitate: boolean;
  collaborative: boolean;
  getContainer: () => HTMLElement | undefined;
}

const EmbeddedWhiteboardPlugin = (
  options: EmbeddedWhiteboardPluginOptions
): EditorPlugin[] => {
  const sharingOptions = options.sharingOptions || {};

  const EmbeddedWhiteboardControllersPlugin =
    EmbeddedWhiteboardControllersPluginFactory();

  const EmbeddedWhiteboard: React.FC<
    ComponentProps<EmbeddedWhiteboardElement>
  > = ({ element, ...props }) => {
    return (
      <EmbeddedWhiteboardComponent
        {...props}
        element={element}
        defaultProject={options.defaultProject}
        sharingOptions={sharingOptions}
        commentsEnabled={options.commentsEnabled}
        canFacilitate={options.canFacilitate || false}
        collaborative={options.collaborative}
        containingPageId={options.containingPageId}
        setController={(controller: WhiteboardContainerController | null) => {
          if (controller) {
            EmbeddedWhiteboardControllersPlugin.api.setWhiteboardController(
              element.whiteboard_id,
              controller
            );
          } else {
            EmbeddedWhiteboardControllersPlugin.api.removeWhiteboardController(
              element.whiteboard_id
            );
          }
        }}
      />
    );
  };

  return [
    {
      name: 'Embedded whiteboard placeholder component',
      components: [
        {
          type: 'embedded-whiteboard-placeholder',
          component: EmbeddedWhiteboardPlaceholderComponent,
        },
      ],
    },
    {
      id: 'embeddedWhiteboard',
      name: 'Embedded whiteboard component',
      component: {
        type: 'embedded-whiteboard',
        component: withHoverToolbar(EmbeddedWhiteboard, [ToolOpenWhiteboard]),
        toolbarItems: [
          [ToolOpenOriginalWhiteboard],
          [ToolEditWhiteboard],
          [ToolShowWhiteboardComments],
          [ToolDeleteWhiteboard],
        ],
      },
    },
    AllPlugins.EmbeddedWhiteboardSchemaPlugin,
    {
      name: 'Embedded whiteboard insertWhiteboard trigger',
      handlers: {
        onMount(ahaEditor) {
          const container = options.getContainer();

          container?.addEventListener(
            'aha-editor:insert-whiteboard',
            function () {
              const { editor, controller } = ahaEditor;

              // When clicking "Add whiteboard" underneath a description, no whiteboard will be inserted
              // if the user has not clicked into the editor first as selection will be null. If this is
              // the case, we should set the selection at the end of the editor
              if (!editor.selection) {
                slate.Transforms.select(editor, slate.Editor.end(editor, []));
              }

              insertWhiteboard(
                { editor },
                createWhiteboardRecord({
                  objectType: controller.objectType,
                  objectId: controller.objectId,
                  baseURL: controller.baseURL,
                  getSharingToken: controller.getSharingToken,
                }).then(res => res.whiteboard.id)
              );
            }
          );
        },
      },
    },
    EmbeddedWhiteboardPasteTransformerPlugin,
    EmbeddedWhiteboardControllersPlugin,
  ];
};

export default EmbeddedWhiteboardPlugin;
